/**
 * Wat de leerling aan de server vraagt: één functie per /student-endpoint.
 *
 * De stappen van het leerlingscherm bouwden hun aanroepen zelf, met
 * URLSearchParams tussen de opmaak. Hier staat per endpoint welke gegevens
 * erheen gaan en in welke vorm; een stap roept `api.maakGroepje(...)` en
 * krijgt de JSON van de server terug ({ success, data, detail }).
 *
 * De functies gooien niet zelf: een mislukte verbinding komt als fout uit
 * fetch, en de stap beslist wat de leerling dan ziet. Zo houdt elke stap
 * zijn eigen melding op de plek waar hij hoort.
 */
export function maakLeerlingApi(verzoek) {
  const json = async (pad, opties) => (await verzoek(pad, opties)).json();
  const post = (pad, params) => json(pad + '?' + new URLSearchParams(params), { method: 'POST' });

  return {
    haalSessie: (studentId) => json('/student/session?student_id=' + studentId),

    // Een groepsgenoot bevestigt met eigen naam en wachtwoord; het bewijs
    // dat terugkomt gaat mee bij het aanmaken van het groepje. Het
    // wachtwoord gaat in de body, niet in de URL.
    bevestigGroepslid: (sessionId, username, password) => json('/student/groepslid', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, username, password }),
    }),

    maakGroepje: (sessionId, naam, avatar, bewijzen) => {
      const params = { session_id: sessionId, groepje_naam: naam, groepje_avatar: avatar };
      if (bewijzen.length) params.bewijzen = bewijzen.join(',');
      return post('/student/groepje', params);
    },

    haalPersonas: (sessionId) => json('/student/personas?session_id=' + sessionId),
    haalDiscussieleider: () => json('/student/discussieleider'),
    kiesPersonas: (discussionId, personaIds) =>
      post('/student/personas/select', { discussion_id: discussionId, persona_ids: personaIds.join(',') }),

    // De opening van Socrates (is_start) en elk later bericht aan hem. De
    // eigen tekst gaat apart mee als `bericht`, zodat het taalfilter op de
    // server alleen daarnaar kijkt en niet naar de context.
    openingVanSocrates: (discussionId) =>
      post('/student/discussieleider/bericht', { discussion_id: discussionId, context: 'Start discussie', is_start: 'true' }),
    vraagAanSocrates: (discussionId, context, bericht) =>
      post('/student/discussieleider/bericht', { discussion_id: discussionId, context, bericht }),
    eersteReactie: (discussionId) => post('/student/persona/opening', { discussion_id: discussionId }),
    berichtAanPersona: (discussionId, personaId, bericht) =>
      post('/student/chat', { discussion_id: discussionId, persona_id: personaId, bericht }),
    vraagUitleg: (discussionId, tekst) =>
      post('/student/uitleg', { discussion_id: discussionId, selected_text: tekst }),

    // De hulp in de tweede fase: soort is verhelder, vereenvoudig, hints of
    // kritiek. De argumenten gaan mee als regels, één per argument.
    vraagHulp: (discussionId, { soort, tekst, kant, voor, tegen }) =>
      post('/student/socrates/hulp', {
        discussion_id: discussionId, soort, tekst: tekst || '', kant: kant || '',
        argumenten_voor: voor.join('\n'), argumenten_tegen: tegen.join('\n'),
      }),
    haalBronnen: (discussionId) => json('/student/bronnen?' + new URLSearchParams({ discussion_id: discussionId })),

    slaArgumentenOp: (discussionId, voor, tegen) =>
      post('/student/argumenten', { discussion_id: discussionId, argumenten_voor: voor.join('\n'), argumenten_tegen: tegen.join('\n') }),
    rondAf: (discussionId) => post('/student/afronden', { discussion_id: discussionId }),
  };
}
